import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { makeStyles, mergeClasses, shorthands, tokens } from '@fluentui/react-components';

/**
 * A docking split panel.
 *
 * Fluent v9 ships no split/resizable-pane primitive and the package feeds are unreachable from this
 * environment, so this is a small local control rather than a dependency.
 *
 * It docks: given a bounded parent it fills that parent exactly and both panes become independent
 * scroll regions, so callers stop computing heights. The layout is CSS Grid rather than flex because
 * a grid track is a true fill primitive — `minmax(0, 1fr)` resolves against the container, so
 * children adjust to the container instead of the container growing to fit its children. The `0`
 * floor in each `minmax` is what lets a pane shrink below its content and scroll; without it grid
 * uses min-content as the floor and the pane silently pushes the layout past the viewport.
 *
 * The divider is draggable because a stack trace and a deep span tree want opposite widths and no
 * fixed ratio suits both.
 */
const useStyles = makeStyles({
  root: {
    display: 'grid',
    // One fill row, so the panes take exactly the docked height.
    gridTemplateRows: 'minmax(0, 1fr)',
    height: '100%',
    minHeight: 0,
    minWidth: 0,
  },
  pane: {
    minWidth: 0,
    minHeight: 0,
    overflow: 'auto',
  },
  // Wide hit target, thin visible rule: easy to grab, quiet to look at.
  divider: {
    width: '10px',
    cursor: 'col-resize',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ':focus-visible': { outline: `2px solid ${tokens.colorStrokeFocus2}` },
  },
  dividerRule: {
    width: '2px',
    height: '100%',
    backgroundColor: tokens.colorNeutralStroke2,
    ...shorthands.borderRadius('1px'),
  },
  dividerRuleActive: { backgroundColor: tokens.colorBrandStroke1 },
});

export interface SplitPaneProps {
  primary: React.ReactNode;
  secondary: React.ReactNode;
  /** Secondary pane width as a percentage of the split; the starting position. */
  defaultSecondaryPercent?: number;
  /** Clamps so neither pane can be dragged into uselessness. */
  minSecondaryPercent?: number;
  maxSecondaryPercent?: number;
  secondaryLabel?: string;
  className?: string;
}

export function SplitPane({
  primary,
  secondary,
  defaultSecondaryPercent = 30,
  minSecondaryPercent = 18,
  maxSecondaryPercent = 60,
  secondaryLabel = 'details panel',
  className,
}: SplitPaneProps) {
  const styles = useStyles();
  const rootRef = useRef<HTMLDivElement>(null);
  const [percent, setPercent] = useState(defaultSecondaryPercent);
  const [dragging, setDragging] = useState(false);
  const separatorId = useId();

  const clamp = useCallback(
    (v: number) => Math.min(maxSecondaryPercent, Math.max(minSecondaryPercent, v)),
    [minSecondaryPercent, maxSecondaryPercent],
  );

  // Pointer handlers live on window, not the divider: the pointer routinely leaves a 10px target
  // mid-drag, and a listener bound to the divider would drop the gesture when it does.
  useEffect(() => {
    if (!dragging) return undefined;

    const onMove = (e: PointerEvent) => {
      const el = rootRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      if (rect.width === 0) return;
      setPercent(clamp(((rect.right - e.clientX) / rect.width) * 100));
    };
    const stop = () => setDragging(false);

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', stop);
    window.addEventListener('pointercancel', stop);
    // Without this the drag paints a text selection across the whole page.
    const priorSelect = document.body.style.userSelect;
    document.body.style.userSelect = 'none';

    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', stop);
      window.removeEventListener('pointercancel', stop);
      document.body.style.userSelect = priorSelect;
    };
  }, [dragging, clamp]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') { setPercent((p) => clamp(p + 2)); e.preventDefault(); }
    if (e.key === 'ArrowRight') { setPercent((p) => clamp(p - 2)); e.preventDefault(); }
    if (e.key === 'Home') { setPercent(clamp(defaultSecondaryPercent)); e.preventDefault(); }
  };

  return (
    <div
      ref={rootRef}
      className={mergeClasses(styles.root, className)}
      // Columns rather than flex-basis: the primary track absorbs the remainder, so the secondary
      // pane is sized purely as a percentage and its contents adjust without any measuring.
      style={{ gridTemplateColumns: `minmax(0, 1fr) auto minmax(0, ${percent}%)` }}
    >
      <div className={styles.pane}>{primary}</div>
      <div
        id={separatorId}
        role="separator"
        aria-orientation="vertical"
        aria-label={`Resize ${secondaryLabel}`}
        aria-valuenow={Math.round(percent)}
        aria-valuemin={minSecondaryPercent}
        aria-valuemax={maxSecondaryPercent}
        tabIndex={0}
        className={styles.divider}
        onPointerDown={(e) => { e.preventDefault(); setDragging(true); }}
        onKeyDown={onKeyDown}
      >
        <span className={mergeClasses(styles.dividerRule, dragging && styles.dividerRuleActive)} />
      </div>
      <div className={styles.pane}>{secondary}</div>
    </div>
  );
}
